import React from "react";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";

import PageHeader from "../Common/PageHeader";
import Loader from "../Common/Loader";
import NotFoundPage from "../Common/NotFoundPage";
const DocumentLibraryViewer = React.lazy(() => import("./DocumentLibraryViewer"));
import DocumentLibraryPreviewPopin from "./DocumentLibraryPreviewPopin";
import DocumentLibraryDetailSidebar from "./DocumentLibraryDetailSidebar";
import DocumentLibraryApi from "api/DocumentLibraryApi";
import { getDocumentLibraryByRoute, getDocumentLibraryGroupByParent } from "helper/documentModules";
import { withModulesData, type ModulesQueryProps } from "api/queries/useModules";
import { getFileExtension, getPreviewKind } from "helper/file";
import { normalizeLanguage } from "config/i18n";
import { isAllowed } from "helper/acl";
import { withAuthData, type AuthActions } from "../../context/AuthContext";
import { getApiData, getApiErrorCode, type ApiResponse } from "../../types/api";

const NotFoundPageAny = NotFoundPage as unknown as React.ComponentType<Record<string, unknown>>;

interface DocumentLibraryDocument {
  id?: number | string;
  title?: string;
  lang?: string;
  original_filename?: string;
  extension?: string;
  mime?: string;
}

interface Library {
  routePath?: string;
  title?: string;
  titleKey?: string;
  parentSlug?: string;
  slug?: string;
  frontPermission?: string;
}

interface DocumentFileState {
  data: Uint8Array | null;
  filename?: string;
  mime?: string;
  previewData?: Uint8Array | null;
  size?: number;
  title?: string;
  preview?: unknown;
  previewStatus?: string;
  previewFile?: { filename?: string; mime?: string } | null;
}

interface DocumentLibraryDetailDocumentOwnProps extends WithTranslation {
  params?: { parentSlug?: string; moduleSlug?: string; documentId?: string };
  location?: { state?: { document?: DocumentLibraryDocument } };
  library?: Library;
  document?: DocumentLibraryDocument;
  documentId?: number | string;
  embedded?: boolean;
}

interface DocumentLibraryDetailDocumentDispatchProps {
  sessionActions: AuthActions;
}

type DocumentLibraryDetailDocumentProps = DocumentLibraryDetailDocumentOwnProps &
  ModulesQueryProps &
  DocumentLibraryDetailDocumentDispatchProps;

interface DocumentState {
  documentFile: DocumentFileState | null;
  previewSource: string | Uint8Array | null;
  previewUrl: string | null;
  originalUrl: string | null;
  downloadUrl: string | null;
}

function DocumentLibraryDetailDocument(props: DocumentLibraryDetailDocumentProps) {
  const {
    t,
    i18n,
    params,
    location,
    library: libraryProp,
    document: documentProp,
    documentId: documentIdProp,
    modules,
    modulesLoaded,
    embedded,
    sessionActions,
  } = props;

  const currentLanguageKey = normalizeLanguage(i18n?.resolvedLanguage || "fr") || "fr";

  const getLibrary = React.useCallback((): Library | null => {
    if (libraryProp) return libraryProp;
    return (
      (getDocumentLibraryByRoute(
        modules,
        params?.parentSlug,
        params?.moduleSlug
      ) as unknown as Library | null) || null
    );
  }, [libraryProp, modules, params?.parentSlug, params?.moduleSlug]);

  const getDocumentItem = React.useCallback((): DocumentLibraryDocument | null => {
    if (documentProp) return documentProp;
    return location?.state?.document || null;
  }, [documentProp, location]);

  const getDocumentId = React.useCallback((): number | string | null => {
    return documentIdProp || params?.documentId || null;
  }, [documentIdProp, params?.documentId]);

  const [isLoading, setIsLoading] = React.useState(true);
  const [documentState, setDocumentState] = React.useState<DocumentState>({
    documentFile: null,
    previewSource: null,
    previewUrl: null,
    originalUrl: null,
    downloadUrl: null,
  });
  const [languageKey, setLanguageKey] = React.useState<string>(currentLanguageKey);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);
  const [availableLanguages, setAvailableLanguages] = React.useState<string[]>([]);
  const [displayedLanguageKey, setDisplayedLanguageKey] = React.useState<string | null>(null);

  const objectUrlsRef = React.useRef<Set<string>>(new Set());

  const destroyPreviewUrl = React.useCallback(() => {
    objectUrlsRef.current.forEach((objectUrl) => window.URL.revokeObjectURL(objectUrl));
    objectUrlsRef.current.clear();
  }, []);

  const getMimeType = React.useCallback((): string => {
    return documentState.documentFile?.mime || "application/octet-stream";
  }, [documentState.documentFile]);

  const buildDocumentState = React.useCallback(
    (response: ApiResponse<Record<string, unknown>>) => {
      const content = getApiData(response, {}) as Record<string, unknown> & {
        preview_file?: {
          filename?: string;
          mime?: string;
          filedata?: string;
          stream_url?: string;
        } | null;
        stream_url?: string;
        download_url?: string;
        filedata?: string;
        filename?: string;
        mime?: string;
        size?: number;
        title?: string;
        preview?: unknown;
        preview_status?: string;
      };
      const previewFile = content.preview_file || null;
      const documentFile: DocumentFileState = {
        data: null,
        filename: content.filename,
        mime: content.mime,
        size: content.size,
        title: content.title,
        preview: content.preview || null,
        previewStatus: content.preview_status || "none",
        previewFile: previewFile
          ? { filename: previewFile.filename, mime: previewFile.mime }
          : null,
      };

      if (content.stream_url) {
        const previewStreamUrl = previewFile?.stream_url || content.stream_url;

        return {
          documentFile,
          previewSource: previewStreamUrl,
          previewUrl: previewStreamUrl,
          originalUrl: content.stream_url,
          downloadUrl: content.download_url || content.stream_url,
        };
      }

      const originalData = Uint8Array.from(atob(content.filedata || ""), (char) =>
        char.charCodeAt(0)
      );
      documentFile.data = originalData;

      const originalBlob = new Blob([originalData], {
        type: documentFile.mime || getMimeType(),
      });
      const originalUrl = window.URL.createObjectURL(originalBlob);
      objectUrlsRef.current.add(originalUrl);

      let previewUrl = originalUrl;
      let previewSource: string | Uint8Array = originalUrl;
      if (previewFile && previewFile.filedata) {
        const previewData = Uint8Array.from(atob(previewFile.filedata), (char) =>
          char.charCodeAt(0)
        );
        documentFile.previewData = previewData;
        const previewBlob = new Blob([previewData], {
          type: previewFile.mime || "application/octet-stream",
        });
        previewUrl = window.URL.createObjectURL(previewBlob);
        previewSource = previewData;
        objectUrlsRef.current.add(previewUrl);
      }

      return {
        documentFile,
        previewSource,
        previewUrl,
        originalUrl,
        downloadUrl: content.download_url || originalUrl,
      };
    },
    [getMimeType]
  );

  const loadFallbackDocument = React.useCallback(
    (langs: string[] = []) => {
      const fallbackLanguageKey =
        langs.includes("en") && languageKey !== "en"
          ? "en"
          : langs.find((lk) => lk && lk !== languageKey);
      if (!fallbackLanguageKey) {
        setIsLoading(false);
        setErrorCode("DOCUMENT_TRANSLATION_NOT_FOUND");
        setAvailableLanguages(langs);
        setDisplayedLanguageKey(null);
        return;
      }

      const docId = getDocumentId();
      if (docId == null) return;

      DocumentLibraryApi.getFile(docId, fallbackLanguageKey)
        .then((response) => {
          const next = buildDocumentState(response as ApiResponse<Record<string, unknown>>);
          setIsLoading(false);
          setErrorCode("DOCUMENT_TRANSLATION_NOT_FOUND");
          setAvailableLanguages(langs);
          setDisplayedLanguageKey(fallbackLanguageKey);
          setDocumentState(next);
        })
        .catch(() => {
          setIsLoading(false);
          setErrorCode("DOCUMENT_TRANSLATION_NOT_FOUND");
          setAvailableLanguages(langs);
          setDisplayedLanguageKey(null);
        });
    },
    [buildDocumentState, getDocumentId, languageKey]
  );

  const loadDocument = React.useCallback(() => {
    const library = getLibrary();

    setIsLoading(true);
    setDocumentState({
      documentFile: null,
      previewSource: null,
      previewUrl: null,
      originalUrl: null,
      downloadUrl: null,
    });
    setErrorCode(null);
    setAvailableLanguages([]);
    setDisplayedLanguageKey(null);

    if (!library || !isAllowed(library.frontPermission)) {
      setIsLoading(false);
      setErrorCode("DOCUMENT_NOT_FOUND");
      return;
    }

    const docId = getDocumentId();
    if (docId == null) {
      setIsLoading(false);
      setErrorCode("DOCUMENT_NOT_FOUND");
      return;
    }

    DocumentLibraryApi.getFile(docId, languageKey || currentLanguageKey)
      .then((response) => {
        const next = buildDocumentState(response as ApiResponse<Record<string, unknown>>);
        setIsLoading(false);
        setAvailableLanguages([]);
        setDisplayedLanguageKey(languageKey);
        setDocumentState(next);
      })
      .catch((error) => {
        if (getApiErrorCode(error) === "NOT_LOGGED") {
          sessionActions.logOutUser();
          return;
        }

        const errorPayload =
          error.data && typeof error.data === "object"
            ? (getApiData(error.data as ApiResponse<{ available_languages?: string[] }>, {}) as {
                available_languages?: string[];
              })
            : {};
        const langs = Array.isArray(errorPayload.available_languages)
          ? errorPayload.available_languages
          : [];

        if (getApiErrorCode(error) === "DOCUMENT_TRANSLATION_NOT_FOUND" && langs.length > 0) {
          loadFallbackDocument(langs);
          return;
        }

        setIsLoading(false);
        setErrorCode(getApiErrorCode(error) || "DOCUMENT_NOT_FOUND");
        setAvailableLanguages(langs);
        setDisplayedLanguageKey(null);
      });
  }, [
    getLibrary,
    getDocumentId,
    currentLanguageKey,
    languageKey,
    buildDocumentState,
    loadFallbackDocument,
    sessionActions,
  ]);

  // Cleanup object URLs on unmount
  React.useEffect(() => {
    return () => {
      destroyPreviewUrl();
    };
  }, [destroyPreviewUrl]);

  // Mount + reload on param / module / document id / language change
  const paramKey = `${params?.parentSlug}|${params?.moduleSlug}|${params?.documentId}|${documentIdProp}`;
  React.useEffect(() => {
    if (getLibrary() || modulesLoaded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadDocument();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey, modulesLoaded, languageKey]);

  // Track language change separately
  React.useEffect(() => {
    if (currentLanguageKey !== languageKey) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLanguageKey(currentLanguageKey);
    }
  }, [currentLanguageKey, languageKey]);

  const getBreadcrumbs = (library: Library, title: string) => {
    const group = getDocumentLibraryGroupByParent(modules, library.parentSlug) as {
      titleKey: string;
      libraries: Array<{ routePath?: string }>;
    } | null;
    const firstLibrary = group && group.libraries.length > 0 ? group.libraries[0] : null;

    return [
      { label: t("navigation.label_home"), to: BASE_PATH + "/" },
      group
        ? { label: t(group.titleKey), to: firstLibrary ? firstLibrary.routePath : undefined }
        : null,
      {
        label: library.title || (library.titleKey ? t(library.titleKey) : ""),
        to: library.routePath,
      },
      { label: title },
    ].filter(Boolean) as Array<{ label: string; to?: string }>;
  };

  const library = getLibrary();
  const isEmbedded = !!embedded;

  if (!modulesLoaded && !library) {
    return isEmbedded ? (
      <Loader message={t("common.label_loading")} />
    ) : (
      <div className="Page DocumentLibraryDetailPage">
        <div className="Page-content DocumentLibraryDetailPage-content">
          <Loader />
        </div>
      </div>
    );
  }

  if (!library || errorCode === "DOCUMENT_NOT_FOUND") {
    if (isEmbedded) {
      return <p className="ListMessage">{t("document_library.detail.not_found")}</p>;
    }
    return <NotFoundPageAny />;
  }

  const item = getDocumentItem();
  const fileName = documentState.documentFile?.filename || (item && item.original_filename) || "";
  const title =
    (item && item.title) ||
    documentState.documentFile?.title ||
    fileName ||
    t("document_library.detail.fallback_title");
  const extension =
    getFileExtension(fileName || (item && item.original_filename) || "") ||
    (item && item.extension) ||
    "";
  const previewFile = documentState.documentFile?.previewFile;
  const kind = getPreviewKind({
    mime: documentState.documentFile?.mime || (item && item.mime) || "",
    originalExtension: extension,
    previewExtension: getFileExtension((previewFile && previewFile.filename) || ""),
    previewMime: (previewFile && previewFile.mime) || "",
  });
  const kindLabel = extension
    ? String(extension).toUpperCase()
    : t(`document_library.detail.types.${kind}`);
  const breadcrumbs = getBreadcrumbs(library, title);
  const previewTitleKey =
    kind === "presentation"
      ? "document_library.detail.preview_title_presentation"
      : "document_library.detail.preview_title_default";
  const shouldShowPopinActions =
    kind !== "pdf" && kind !== "image" && kind !== "video" && !documentState.documentFile?.preview;
  const requestedLanguageLabel = t(`profile.parameters.language_${languageKey}`);
  const availableLanguageLabels = availableLanguages
    .map((lk) => t(`profile.parameters.language_${lk}`))
    .filter((label, index, labels) => label && labels.indexOf(label) === index);
  const missingLanguageMessage =
    errorCode === "DOCUMENT_TRANSLATION_NOT_FOUND"
      ? t(
          availableLanguageLabels.length > 0
            ? "document_library.detail.missing_language"
            : "document_library.detail.missing_language_no_available",
          {
            requestedLanguage: requestedLanguageLabel,
            availableLanguages: availableLanguageLabels.join(", "),
          }
        )
      : null;
  // displayedLanguageKey is exposed for callers but the current render does not use it directly
  void displayedLanguageKey;

  const viewer = (
    <>
      {missingLanguageMessage ? (
        <div className="ComposedForm-messageLine error">{missingLanguageMessage}</div>
      ) : null}
      <React.Suspense fallback={<Loader />}>
        <DocumentLibraryViewer
          t={t}
          isLoading={isLoading}
          documentFile={
            documentState.documentFile as unknown as {
              preview?: {
                type?: string;
                rows?: Array<Array<React.ReactNode>>;
                lines?: string[];
              } | null;
            } | null
          }
          previewSource={documentState.previewSource}
          previewUrl={documentState.previewUrl}
          kind={kind}
          fileName={fileName}
        />
      </React.Suspense>
    </>
  );

  if (isEmbedded) {
    return (
      <DocumentLibraryPreviewPopin
        title={title}
        kindLabel={kindLabel}
        fileName={fileName}
        viewer={viewer}
        openUrl={shouldShowPopinActions ? documentState.originalUrl : null}
        downloadUrl={shouldShowPopinActions ? documentState.downloadUrl : null}
        openDocumentLabel={t("document_library.detail.open_document")}
        downloadLabel={t("common.button_downlaod")}
      />
    );
  }

  return (
    <div className="Page DocumentLibraryDetailPage">
      <div className="Page-content DocumentLibraryDetailPage-content">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title={title}
          text={t("document_library.detail.text")}
          meta={
            <>
              {kindLabel ? (
                <span className="DocumentLibraryDetailHeader-badge">{kindLabel}</span>
              ) : null}
              {fileName ? (
                <span className="DocumentLibraryDetailHeader-badge DocumentLibraryDetailHeader-badge--file">
                  {fileName}
                </span>
              ) : null}
            </>
          }
          metaClassName="PageHero-meta--badges"
        />
        <div className="DocumentLibraryDetailLayout">
          <section className="DocumentLibraryDetailPanel DocumentLibraryDetailPanel--viewer">
            <div className="DocumentLibraryDetailPanel-header">
              <div>
                <p className="DocumentLibraryDetailPanel-kicker">
                  {t("document_library.detail.preview_kicker")}
                </p>
                <h2 className="DocumentLibraryDetailPanel-title">{t(previewTitleKey)}</h2>
              </div>
              {extension ? (
                <span className="DocumentLibraryCard-format doc-type-badge">{extension}</span>
              ) : null}
            </div>
            {viewer}
          </section>
          <DocumentLibraryDetailSidebar
            t={t}
            item={item as { publication_start?: string } | null}
            kindLabel={kindLabel}
            fileName={fileName}
            title={title}
            previewUrl={documentState.previewUrl}
            originalUrl={documentState.originalUrl}
            downloadUrl={documentState.downloadUrl}
            library={library}
            language={i18n.resolvedLanguage}
          />
        </div>
      </div>
    </div>
  );
}

export default compose(
  withTranslation(),
  withAuthData,
  withModulesData
)(DocumentLibraryDetailDocument);
